/**
 * Format a date string to just day, month and year.
 *
 * @param dateOrMillis date ISO date string or milliseconds since epoch
 */
const formatDate = (dateOrMillis: string | number) => new Date(dateOrMillis).toLocaleDateString();

/**
 * Format a date to "Month Year". E.g., `Nov 2019`.
 * @param millis milliseconds since epoch
 */
const formatMonthYear = (millis: number) => {
  const f = new Intl.DateTimeFormat('en', { year: 'numeric', month: 'short' });
  return f.format(new Date(millis));
};

/**
 * Format a hash (e.g., git hash) to an 8 character hash.
 *
 * @param hash hash to shorten
 */
const shortenHash = (hash: string) => hash.slice(0, 8);

/**
 * Format a revision given millis and revision hash.
 * Returns "hash (date)".
 *
 * @param millis milliseconds since epoch
 * @param hash revision hash
 */
const formatRevision = (millis: number, hash: string): string => `${
  shortenHash(hash)} (${formatDate(millis)})`;

/**
 * Format a readability score to two decimals.
 *
 * @param score a readability score
 */
const formatReadabilityScore = (score: number) => score.toFixed(2);

/**
 * Format a number into a delta (add + if it is positive).
 *
 * @param n number
 */
const formatDelta = (n: number): string => (n > 0
  ? `+${n.toFixed(2)}`
  : `${n.toFixed(2)}`);

/**
 * Given a URL, extract the host name.
 *
 * @param url url as string
 */
const getHostFromURL = (url: string): string => new URL(url).hostname;

/**
 * Get the repository name from a git URL.
 *
 * @param url git url
 */
const getRepositoryNameFromGitURL = (url: string): string => new URL(url)
  .pathname
  .replace(/.git$/, '')
  .replace(/^\//, '');

export default {
  formatDate,
  formatMonthYear,
  formatDelta,
  formatReadabilityScore,
  formatRevision,
  getHostFromURL,
  getRepositoryNameFromGitURL,
  shortenHash,
};
